import {fold, fromNullable, map as mapOption} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {DateTime} from 'luxon';
import {VALUE_NOT_AVAILABLE} from './utilities';

export const enum DateTimeFormat {
  DATE = 'MM/dd/yyyy',
  TIME = 'hh:mm a',
}

type DateType = DateTime | Date | string | null | undefined;

/**
 *
 * @param date date value
 * @returns date in ISO format or current date in ISO format if date is undefined
 */

export const getISODateString = (date: DateType): string => {
  let dateTimeValue: DateTime | null = null;

  if (date instanceof DateTime) {
    dateTimeValue = date;
  }

  if (date instanceof Date) {
    dateTimeValue = DateTime.fromJSDate(date);
  }

  if (typeof date === 'string') {
    dateTimeValue = DateTime.fromISO(date);
  }

  if (!dateTimeValue?.isValid) {
    dateTimeValue = null;
  }

  return pipe(
    dateTimeValue,
    fromNullable,
    fold(
      () => VALUE_NOT_AVAILABLE,
      dt => dt.toUTC().toISO()
    )
  );
};

export const getFormattedTime = (date: DateType, utc: boolean = true) => {
  return pipe(
    getDateTimeFromDate(date, utc),
    fold(
      () => VALUE_NOT_AVAILABLE,
      date => date.toFormat(DateTimeFormat.TIME)
    )
  );
};

export const getFormattedDate = (date: DateType, utc: boolean = true) => {
  return pipe(
    getDateTimeFromDate(date),
    fold(
      () => VALUE_NOT_AVAILABLE,
      date => date.toFormat(DateTimeFormat.DATE)
    )
  );
};

const getDateTimeFromDate = (date: DateType, utc: boolean = true) => {
  let jsDate: Date | null = null;

  if (date instanceof Date) {
    jsDate = date;
  } else if (typeof date === 'string') {
    jsDate = new Date(date);
  }
  return pipe(
    fromNullable(jsDate),
    mapOption((d: Date) => (utc ? DateTime.fromJSDate(d).toUTC() : DateTime.fromJSDate(d)))
  );
};

const isValidISODate = (date: string | undefined) =>
  date ? pipe(date, DateTime.fromISO, dateStr => dateStr.isValid) : false;

export const safeRenderDatePickerValue = (date: string | undefined) =>
  date ? pipe(date, isValidISODate, v => (v === true ? DateTime.fromISO(date).toUTC() : null)) : null;

export const getCurrentDate = () => DateTime.local().toFormat(DateTimeFormat.DATE);

export const getCurrentDateISO = () =>
  DateTime.local()
    .toUTC()
    .toISO();

export const getFirstDateOfNextYear = () =>
  DateTime.local()
    .plus({years: 1})
    .startOf('year');
